import { useCallback, useEffect, useRef, useState } from 'react';

// A user-requested analysis can wait for persisted OCR without treating the
// separate Extract all image text action as authorization to run AI analysis.
export const useCarouselReanalysis = ({ messageId, ocrComplete, requestOcr, analyse, onError }) => {
    const [phase, setPhase] = useState('idle');
    const active = useRef(false);
    const generation = useRef(0);

    useEffect(() => {
        generation.current += 1;
        active.current = false;
        setPhase('idle');
        return () => { generation.current += 1; };
    }, [messageId]);

    const runAnalysis = useCallback(async () => {
        const current = generation.current;
        setPhase('analysis');
        try {
            await analyse();
        } catch (error) {
            if (current === generation.current) onError(error);
        } finally {
            if (current === generation.current) {
                active.current = false;
                setPhase('idle');
            }
        }
    }, [analyse, onError]);

    useEffect(() => {
        if (active.current && phase === 'waiting' && ocrComplete) runAnalysis();
    }, [phase, ocrComplete, runAnalysis]);

    const start = useCallback(async () => {
        if (active.current || !messageId) return;
        active.current = true;
        if (ocrComplete) return runAnalysis();
        const current = generation.current;
        setPhase('ocr');
        try {
            await requestOcr();
            if (current === generation.current) setPhase('waiting');
        } catch (error) {
            if (current === generation.current) {
                active.current = false;
                setPhase('idle');
                onError(error);
            }
        }
    }, [messageId, ocrComplete, requestOcr, runAnalysis, onError]);

    return { start, isRunning: phase !== 'idle' };
};
